/**
 * Daily play streaks: home (any mode) + per-mode.
 * Guests: local calendar day (device timezone).
 * Signed-in: POST /api/play-streaks — server clock + account IANA timezone.
 */
import type { AccountDataPayloadV1 } from "../lib/accountDataTypes";
import { postPlayStreakDay, fetchPlayStreaks, type PlayStreaksSyncMeta } from "../lib/cloud";

export type StreakEntry = {
  streak: number;
  lastPlayDate: string | null;
};

export type PlayStreaksState = {
  home: StreakEntry;
  modes: Record<string, StreakEntry>;
};

export type StreakView = {
  streak: number;
  lit: boolean;
  showCount: boolean;
};

const KEY_PLAY_STREAKS = "playStreaksV1";
const KEY_PLAY_STREAK_META = "playStreaksMetaV1";

/** Optional hook to push account data after a local streak change (registered by account-sync). */
let scheduleStreakAccountPush: (() => void) | null = null;

export function setPlayStreakAccountPushScheduler(fn: (() => void) | null) {
  scheduleStreakAccountPush = fn;
}

const emptyEntry = (): StreakEntry => ({ streak: 0, lastPlayDate: null });

type ClientStreakMeta = {
  /** Authoritative "today" from last successful signed-in sync (YYYY-MM-DD). */
  serverToday: string | null;
  timeZone: string | null;
  /** ISO time when the current serverToday ends (next local midnight). */
  dayEndsAt: string | null;
  /** serverNow - Date.now() at last sync (ms), to reduce device-clock drift for UI. */
  clockSkewMs: number;
};

let meta: ClientStreakMeta = {
  serverToday: null,
  timeZone: null,
  dayEndsAt: null,
  clockSkewMs: 0,
};

let dayRolloverTimer: ReturnType<typeof setTimeout> | null = null;

function detectBrowserTimeZone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && typeof tz === "string") return tz;
  } catch {
    /* ignore */
  }
  return "UTC";
}

function isValidTimeZoneId(tz: string): boolean {
  try {
    Intl.DateTimeFormat("en-US", { timeZone: tz }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

/** Calendar date YYYY-MM-DD in an IANA time zone. */
export function calendarDateKey(d = new Date(), timeZone?: string | null): string {
  const tz =
    timeZone && isValidTimeZoneId(timeZone) ? timeZone : detectBrowserTimeZone();
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/** Previous civil calendar day for a YYYY-MM-DD key. */
export function previousDateKey(key: string): string {
  const t = Date.parse(`${key}T12:00:00.000Z`);
  if (!Number.isFinite(t)) return key;
  return calendarDateKey(new Date(t - 86400000), "UTC");
}

function nextDateKey(key: string): string {
  const t = Date.parse(`${key}T12:00:00.000Z`);
  if (!Number.isFinite(t)) return key;
  return calendarDateKey(new Date(t + 86400000), "UTC");
}

/** @deprecated use calendarDateKey */
export function utcDateKey(d = new Date()): string {
  return calendarDateKey(d, "UTC");
}

/** @deprecated use previousDateKey */
export function previousUtcDateKey(key: string): string {
  return previousDateKey(key);
}

/** Local (browser) calendar day — guests and UI fallback. */
export function localDateKey(d = new Date()): string {
  return calendarDateKey(d, detectBrowserTimeZone());
}

/** @deprecated use previousDateKey */
export function previousLocalDateKey(key: string): string {
  return previousDateKey(key);
}

function correctedNow(): Date {
  return new Date(Date.now() + (meta.clockSkewMs || 0));
}

/**
 * "Today" for streak UI / guest bumps.
 * Signed-in: prefer last server-provided day (with rollover via dayEndsAt / skew).
 * Guests: browser local calendar day.
 */
export function streakTodayKey(signedInHint = false): string {
  const useServer = signedInHint || !!meta.serverToday;
  if (useServer && meta.serverToday) {
    const now = correctedNow();
    if (meta.dayEndsAt) {
      const ends = Date.parse(meta.dayEndsAt);
      if (Number.isFinite(ends) && now.getTime() >= ends) {
        // Local midnight passed since last sync — new day not yet completed.
        const tz = meta.timeZone || detectBrowserTimeZone();
        return calendarDateKey(now, tz);
      }
    }
    const tz = meta.timeZone || detectBrowserTimeZone();
    const computed = calendarDateKey(now, tz);
    // If skew-corrected now still matches stored today, keep server today.
    if (computed === meta.serverToday) return meta.serverToday;
    return computed;
  }
  return localDateKey();
}

function readMeta(): void {
  try {
    const raw = localStorage.getItem(KEY_PLAY_STREAK_META);
    if (!raw) return;
    const o = JSON.parse(raw) as Partial<ClientStreakMeta>;
    meta = {
      serverToday: typeof o.serverToday === "string" ? o.serverToday : null,
      timeZone: typeof o.timeZone === "string" ? o.timeZone : null,
      dayEndsAt: typeof o.dayEndsAt === "string" ? o.dayEndsAt : null,
      clockSkewMs: Number.isFinite(Number(o.clockSkewMs)) ? Number(o.clockSkewMs) : 0,
    };
  } catch {
    /* ignore */
  }
}

function writeMeta() {
  try {
    localStorage.setItem(KEY_PLAY_STREAK_META, JSON.stringify(meta));
  } catch {
    /* ignore */
  }
}

function applySyncMeta(sync: PlayStreaksSyncMeta | null | undefined) {
  if (!sync?.today) return;
  meta.serverToday = sync.today;
  if (typeof sync.timeZone === "string" && sync.timeZone) meta.timeZone = sync.timeZone;
  if (typeof sync.dayEndsAt === "string") meta.dayEndsAt = sync.dayEndsAt;
  if (typeof sync.serverNow === "string") {
    const serverMs = Date.parse(sync.serverNow);
    if (Number.isFinite(serverMs)) meta.clockSkewMs = serverMs - Date.now();
  }
  writeMeta();
  scheduleDayRollover();
}

function scheduleDayRollover() {
  if (dayRolloverTimer != null) {
    clearTimeout(dayRolloverTimer);
    dayRolloverTimer = null;
  }
  if (!meta.dayEndsAt) return;
  const ends = Date.parse(meta.dayEndsAt);
  if (!Number.isFinite(ends)) return;
  const delay = ends - correctedNow().getTime() + 50;
  if (delay > 48 * 3600000) return;
  if (delay <= 0) {
    // Already past — refresh UI to dim the flame for the new day.
    refreshAllPlayStreakUi();
    return;
  }
  dayRolloverTimer = setTimeout(() => {
    dayRolloverTimer = null;
    // New local day: flame dims until the user plays again (streak count kept until decay).
    refreshAllPlayStreakUi();
    // Pull server today so meta stays authoritative when online.
    void syncPlayStreaksFromServer();
  }, Math.min(delay, 2147483647));
}

readMeta();
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      scheduleDayRollover();
      refreshAllPlayStreakUi();
    }
  });
}

function clampEntry(raw: unknown): StreakEntry {
  if (!raw || typeof raw !== "object") return emptyEntry();
  const o = raw as Record<string, unknown>;
  const streak = Math.max(0, Math.floor(Number(o.streak) || 0));
  const lastPlayDate =
    typeof o.lastPlayDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(o.lastPlayDate)
      ? o.lastPlayDate
      : null;
  if (!streak || !lastPlayDate) return emptyEntry();
  return { streak, lastPlayDate };
}

function decayEntry(entry: StreakEntry, today: string, yesterday: string): StreakEntry {
  if (!entry.lastPlayDate || entry.streak <= 0) return emptyEntry();
  if (entry.lastPlayDate === today || entry.lastPlayDate === yesterday) return entry;
  if (entry.lastPlayDate === nextDateKey(today)) return entry;
  return emptyEntry();
}

export function normalizePlayStreaks(raw: unknown, todayKey?: string): PlayStreaksState {
  const today = todayKey || streakTodayKey();
  const yesterday = previousDateKey(today);
  const src = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const modesIn =
    src.modes && typeof src.modes === "object" ? (src.modes as Record<string, unknown>) : {};
  const modes: Record<string, StreakEntry> = {};
  for (const [id, v] of Object.entries(modesIn)) {
    if (!id) continue;
    const next = decayEntry(clampEntry(v), today, yesterday);
    if (next.streak > 0) modes[id] = next;
  }
  return {
    home: decayEntry(clampEntry(src.home), today, yesterday),
    modes,
  };
}

export function mergePlayStreaks(
  a: AccountDataPayloadV1["playStreaks"] | null | undefined,
  b: AccountDataPayloadV1["playStreaks"] | null | undefined,
): PlayStreaksState {
  // Merge without decaying so account sync does not wipe progress.
  const normalizeRaw = (raw: unknown): PlayStreaksState => {
    const src = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    const modesIn =
      src.modes && typeof src.modes === "object" ? (src.modes as Record<string, unknown>) : {};
    const modes: Record<string, StreakEntry> = {};
    for (const [id, v] of Object.entries(modesIn)) {
      if (!id) continue;
      const next = clampEntry(v);
      if (next.streak > 0) modes[id] = next;
    }
    return { home: clampEntry(src.home), modes };
  };
  const left = normalizeRaw(a ?? null);
  const right = normalizeRaw(b ?? null);
  const pick = (x: StreakEntry, y: StreakEntry): StreakEntry => {
    if (x.streak > y.streak) return x;
    if (y.streak > x.streak) return y;
    if (!x.lastPlayDate) return y.streak > 0 ? y : x;
    if (!y.lastPlayDate) return x;
    return x.lastPlayDate >= y.lastPlayDate ? x : y;
  };
  const modes: Record<string, StreakEntry> = {};
  for (const id of new Set([...Object.keys(left.modes), ...Object.keys(right.modes)])) {
    const next = pick(left.modes[id] || emptyEntry(), right.modes[id] || emptyEntry());
    if (next.streak > 0) modes[id] = next;
  }
  const home = pick(left.home, right.home);
  return {
    home: home.streak > 0 ? home : emptyEntry(),
    modes,
  };
}

function readRaw(): unknown {
  try {
    const raw = localStorage.getItem(KEY_PLAY_STREAKS);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const KEY_CAL_MIGRATION = "playStreaksCalV2";

/** One-time guest remap: prior builds stored UTC day keys; map onto browser-local today/yesterday. */
function maybeMigrateGuestUtcDates() {
  try {
    if (localStorage.getItem(KEY_CAL_MIGRATION)) return;
    if (meta.serverToday) {
      localStorage.setItem(KEY_CAL_MIGRATION, "1");
      return;
    }
    const raw = readRaw();
    if (!raw || typeof raw !== "object") {
      localStorage.setItem(KEY_CAL_MIGRATION, "1");
      return;
    }
    const now = new Date();
    const utcToday = calendarDateKey(now, "UTC");
    const utcYesterday = previousDateKey(utcToday);
    const localToday = localDateKey(now);
    const localYesterday = previousDateKey(localToday);
    const remapEntry = (e: StreakEntry): StreakEntry => {
      if (!e.lastPlayDate || e.streak <= 0) return emptyEntry();
      if (e.lastPlayDate === utcToday) return { streak: e.streak, lastPlayDate: localToday };
      if (e.lastPlayDate === utcYesterday) return { streak: e.streak, lastPlayDate: localYesterday };
      return e;
    };
    const src = raw as Record<string, unknown>;
    const modesIn =
      src.modes && typeof src.modes === "object" ? (src.modes as Record<string, unknown>) : {};
    const modes: Record<string, StreakEntry> = {};
    for (const [id, v] of Object.entries(modesIn)) {
      const next = remapEntry(clampEntry(v));
      if (next.streak > 0) modes[id] = next;
    }
    const home = remapEntry(clampEntry(src.home));
    writeState({ home: home.streak > 0 ? home : emptyEntry(), modes });
    localStorage.setItem(KEY_CAL_MIGRATION, "1");
  } catch {
    /* ignore */
  }
}

maybeMigrateGuestUtcDates();

function writeState(state: PlayStreaksState) {
  const payload: PlayStreaksState = {
    home: state.home.streak > 0 ? state.home : emptyEntry(),
    modes: {},
  };
  for (const [id, e] of Object.entries(state.modes)) {
    if (e.streak > 0) payload.modes[id] = e;
  }
  try {
    localStorage.setItem(KEY_PLAY_STREAKS, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function getPlayStreaksState(): PlayStreaksState {
  const state = normalizePlayStreaks(readRaw());
  writeState(state);
  return state;
}

export function applyPlayStreaksFromAccount(
  payload: AccountDataPayloadV1 | null | undefined,
  opts?: { replace?: boolean },
) {
  if (!payload?.playStreaks && !opts?.replace) return;
  if (typeof payload?.playStreakTimezone === "string" && payload.playStreakTimezone) {
    meta.timeZone = payload.playStreakTimezone;
    writeMeta();
  }
  if (opts?.replace) {
    writeState(normalizePlayStreaks(payload?.playStreaks ?? null));
    return;
  }
  writeState(mergePlayStreaks(getPlayStreaksState(), payload?.playStreaks));
}

export function clearPlayStreaksLocal() {
  try {
    localStorage.removeItem(KEY_PLAY_STREAKS);
    localStorage.removeItem(KEY_PLAY_STREAK_META);
    localStorage.removeItem(KEY_CAL_MIGRATION);
  } catch {
    /* ignore */
  }
  meta = { serverToday: null, timeZone: null, dayEndsAt: null, clockSkewMs: 0 };
  if (dayRolloverTimer != null) {
    clearTimeout(dayRolloverTimer);
    dayRolloverTimer = null;
  }
}

function bumpEntry(entry: StreakEntry, today: string, yesterday: string): StreakEntry {
  const cur = decayEntry(entry, today, yesterday);
  if (cur.lastPlayDate === today) return cur;
  if (cur.lastPlayDate === nextDateKey(today)) {
    return { streak: cur.streak, lastPlayDate: today };
  }
  if (cur.lastPlayDate === yesterday) {
    return { streak: cur.streak + 1, lastPlayDate: today };
  }
  return { streak: 1, lastPlayDate: today };
}

/** Guest-only local bump (browser local calendar day). */
export function recordPlayStreakDay(modeId: string | null | undefined): boolean {
  if (!modeId) return false;
  const today = localDateKey();
  const yesterday = previousDateKey(today);
  const state = getPlayStreaksState();
  const prevHome = JSON.stringify(state.home);
  const prevMode = JSON.stringify(state.modes[modeId] || emptyEntry());

  state.home = bumpEntry(state.home, today, yesterday);
  state.modes[modeId] = bumpEntry(state.modes[modeId] || emptyEntry(), today, yesterday);

  const changed =
    JSON.stringify(state.home) !== prevHome ||
    JSON.stringify(state.modes[modeId]) !== prevMode;
  if (changed) writeState(state);
  return changed;
}

/**
 * Record a play day. When `server` is true, prefer API (account TZ + server clock);
 * on failure keep a local bump and schedule account sync.
 * Guests use local calendar-day storage only.
 */
export async function commitPlayStreakDay(
  modeId: string | null | undefined,
  opts?: { server?: boolean },
): Promise<void> {
  if (!modeId) return;
  if (opts?.server) {
    try {
      const res = await postPlayStreakDay(modeId, detectBrowserTimeZone());
      if (res?.playStreaks) {
        applySyncMeta(res);
        writeState(normalizePlayStreaks(res.playStreaks, res.today));
        refreshAllPlayStreakUi();
        return;
      }
    } catch {
      /* fall through to local bump + sync */
    }
    if (recordPlayStreakDay(modeId)) {
      refreshAllPlayStreakUi();
      scheduleStreakAccountPush?.();
    }
    return;
  }
  if (recordPlayStreakDay(modeId)) refreshAllPlayStreakUi();
}

/** Pull server streaks and keep the max vs local; write back to local (+ optional push). */
export async function syncPlayStreaksFromServer(opts?: { pushIfLocalHigher?: boolean }): Promise<boolean> {
  try {
    const remote = await fetchPlayStreaks(detectBrowserTimeZone());
    if (!remote) return false;
    applySyncMeta(remote);
    const before = JSON.stringify(getPlayStreaksState());
    const merged = mergePlayStreaks(getPlayStreaksState(), remote.playStreaks ?? remote);
    writeState(normalizePlayStreaks(merged, remote.today));
    refreshAllPlayStreakUi();
    const after = JSON.stringify(getPlayStreaksState());
    const remoteNorm = normalizePlayStreaks(remote.playStreaks ?? remote, remote.today);
    if (opts?.pushIfLocalHigher && after !== JSON.stringify(remoteNorm)) {
      scheduleStreakAccountPush?.();
    }
    return before !== after;
  } catch {
    return false;
  }
}

export function getHomeStreakView(): StreakView {
  return streakViewFromEntry(getPlayStreaksState().home);
}

export function getModeStreakView(modeId: string): StreakView {
  return streakViewFromEntry(getPlayStreaksState().modes[modeId] || emptyEntry());
}

export function streakViewFromEntry(entry: StreakEntry): StreakView {
  const today = streakTodayKey();
  const yesterday = previousDateKey(today);
  const e = decayEntry(entry, today, yesterday);
  return {
    streak: e.streak,
    lit: e.streak > 0 && e.lastPlayDate === today,
    showCount: e.streak > 0,
  };
}

const FIRE_SVG = `
<svg class="playStreakFireSvg" viewBox="0 0 12 14" width="12" height="14" aria-hidden="true" focusable="false">
  <g class="playStreakFireLayer playStreakFireLayer--a">
    <rect x="5" y="1" width="2" height="1" fill="#ffee66"/>
    <rect x="4" y="2" width="4" height="1" fill="#ffcc33"/>
    <rect x="3" y="3" width="6" height="1" fill="#ff9933"/>
    <rect x="2" y="4" width="8" height="1" fill="#ff7722"/>
    <rect x="2" y="5" width="8" height="1" fill="#ff5511"/>
    <rect x="3" y="6" width="6" height="1" fill="#ee3300"/>
    <rect x="3" y="7" width="6" height="1" fill="#dd2200"/>
    <rect x="4" y="8" width="4" height="1" fill="#cc1100"/>
    <rect x="4" y="9" width="4" height="1" fill="#aa0000"/>
    <rect x="5" y="10" width="2" height="1" fill="#880000"/>
    <rect x="5" y="4" width="2" height="2" fill="#fff3a0"/>
    <rect x="5" y="6" width="2" height="1" fill="#ffe066"/>
  </g>
  <g class="playStreakFireLayer playStreakFireLayer--b">
    <rect x="4" y="1" width="2" height="1" fill="#ffee66"/>
    <rect x="3" y="2" width="5" height="1" fill="#ffcc33"/>
    <rect x="2" y="3" width="7" height="1" fill="#ff9933"/>
    <rect x="2" y="4" width="8" height="1" fill="#ff7722"/>
    <rect x="3" y="5" width="7" height="1" fill="#ff5511"/>
    <rect x="3" y="6" width="6" height="1" fill="#ee3300"/>
    <rect x="4" y="7" width="5" height="1" fill="#dd2200"/>
    <rect x="4" y="8" width="4" height="1" fill="#cc1100"/>
    <rect x="5" y="9" width="3" height="1" fill="#aa0000"/>
    <rect x="5" y="10" width="2" height="1" fill="#880000"/>
    <rect x="5" y="3" width="2" height="2" fill="#fff3a0"/>
    <rect x="4" y="5" width="2" height="2" fill="#ffe066"/>
  </g>
</svg>`.trim();

export function createPlayStreakFireEl(): HTMLElement {
  const wrap = document.createElement("span");
  wrap.className = "playStreakFire";
  wrap.setAttribute("aria-hidden", "true");
  wrap.innerHTML = FIRE_SVG;
  return wrap;
}

export function applyStreakViewToBadge(badge: HTMLElement, view: StreakView, labelPrefix: string) {
  badge.classList.toggle("playStreakBadge--lit", view.lit);
  badge.classList.toggle("playStreakBadge--dim", !view.lit);
  let count = badge.querySelector<HTMLElement>(".playStreakCount");
  if (!count) {
    count = document.createElement("span");
    count.className = "playStreakCount";
    badge.appendChild(count);
  }
  if (view.showCount) {
    count.textContent = String(view.streak);
    count.classList.remove("hidden");
  } else {
    count.textContent = "";
    count.classList.add("hidden");
  }
  const label = view.showCount
    ? `${labelPrefix}: ${view.streak} day${view.streak === 1 ? "" : "s"}${view.lit ? "" : " (play today to keep)"}`
    : `${labelPrefix}: none`;
  badge.setAttribute("aria-label", label);
  badge.title = label;
}

function ensurePlayStreakBadgeUi(root: HTMLElement | null) {
  if (!root) return;
  if (!root.querySelector(".playStreakFire")) {
    root.prepend(createPlayStreakFireEl());
  }
  if (!root.querySelector(".playStreakCount")) {
    const count = document.createElement("span");
    count.className = "playStreakCount";
    root.appendChild(count);
  }
  applyStreakViewToBadge(root, getHomeStreakView(), "Play streak");
}

export function ensureHomePlayStreakUi() {
  ensurePlayStreakBadgeUi(document.getElementById("homePlayStreak"));
}

/** Sidebar badge used when Settings → Show Streak is off (home badge hidden). */
export function ensureSideNavPlayStreakUi() {
  ensurePlayStreakBadgeUi(document.getElementById("sideNavPlayStreak"));
}

export function syncModePlayStreakBadges(boxes: Iterable<HTMLElement>) {
  for (const box of boxes) {
    const id = box.getAttribute("data-mode-id") || "";
    if (!id) continue;
    let badge = box.querySelector<HTMLElement>(".modeBoxPlayStreak");
    if (!badge) {
      badge = document.createElement("div");
      badge.className = "modeBoxPlayStreak playStreakBadge";
      badge.appendChild(createPlayStreakFireEl());
      const count = document.createElement("span");
      count.className = "playStreakCount";
      badge.appendChild(count);
      box.appendChild(badge);
    }
    applyStreakViewToBadge(badge, getModeStreakView(id), `${id} streak`);
  }
}

export function refreshAllPlayStreakUi() {
  ensureHomePlayStreakUi();
  ensureSideNavPlayStreakUi();
  syncModePlayStreakBadges(
    document.querySelectorAll<HTMLElement>("#modesContainer .modeBox[data-mode-id]"),
  );
}
